<template>
    <div class="panel panel-default" data-testid="order-detail-lines">
        <div class="panel-heading">
            <h4>{{ order.ui.labels.lines }}</h4>
        </div>
        <div class="panel-body">
            <p v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</p>
            <p v-if="successMessage" class="alert alert-success">{{ successMessage }}</p>
            <div class="table-responsive">
            <table class="table table-striped table-hover order-detail-v1-data-table">
                <thead>
                    <tr>
                        <th>EAN</th>
                        <th>Artista</th>
                        <th>Titolo</th>
                        <th class="text-right">Qtà</th>
                        <th class="text-right">Lordo</th>
                        <th class="text-right">Sconto</th>
                        <th class="text-right">Netto</th>
                        <th class="text-right">Annullata</th>
                        <th v-if="canMutate">Azioni</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="line in order.lines" :key="line.lineId">
                        <td><code>{{ line.ean || '—' }}</code></td>
                        <td>{{ line.artist }}</td>
                        <td>{{ line.title }}</td>
                        <td class="text-right">
                            <template v-if="canMutate && !line.cancelled && editingLineId === line.lineId">
                                <input
                                    v-model.number="editQty"
                                    type="number"
                                    min="0"
                                    class="form-control input-sm"
                                    style="width: 72px; display: inline-block;"
                                >
                            </template>
                            <template v-else>
                                {{ line.qty }}
                            </template>
                        </td>
                        <td class="text-right text-nowrap">{{ line.grossPriceFormatted }}</td>
                        <td class="text-right">
                            {{ line.discountPercent === null || line.discountPercent === undefined ? '—' : line.discountPercent + '%' }}
                        </td>
                        <td class="text-right text-nowrap"><strong>{{ line.netPriceFormatted }}</strong></td>
                        <td class="text-right">
                            <span v-if="line.cancelled" class="od-badge od-badge--error">Sì</span>
                            <span v-else class="text-muted">No</span>
                        </td>
                        <td v-if="canMutate" class="order-detail-v1-lines__actions">
                            <template v-if="!line.cancelled">
                                <div class="order-detail-v1-lines__action-group">
                                    <template v-if="editingLineId !== line.lineId">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-default"
                                            data-testid="order-detail-reduce-line"
                                            :disabled="busy"
                                            @click="startEdit(line)"
                                        >
                                            Riduci
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-danger"
                                            data-testid="order-detail-line-delete"
                                            :disabled="busy"
                                            @click="cancelLine(line)"
                                        >
                                            Elimina
                                        </button>
                                    </template>
                                    <template v-else>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-primary"
                                            data-testid="order-detail-line-save"
                                            :disabled="busy"
                                            @click="saveReduce(line)"
                                        >
                                            Salva
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-default"
                                            data-testid="order-detail-line-cancel-edit"
                                            :disabled="busy"
                                            @click="cancelEdit"
                                        >
                                            Annulla
                                        </button>
                                    </template>
                                </div>
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'OrderLinesTable',
    props: {
        order: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            busy: false,
            editingLineId: null,
            editQty: 0,
            errorMessage: null,
            successMessage: null,
        };
    },
    computed: {
        canMutate() {
            return !!(this.order.ui && this.order.ui.actions && this.order.ui.actions.canMutateLines)
                && !!(this.order.endpoints && this.order.endpoints.reduceLine);
        },
    },
    methods: {
        startEdit(line) {
            this.editingLineId = line.lineId;
            this.editQty = Math.max(0, Number(line.qty) - 1);
            this.errorMessage = null;
        },
        cancelEdit() {
            this.editingLineId = null;
        },
        async saveReduce(line) {
            await this.postReduce(line.lineId, this.editQty, false);
        },
        async cancelLine(line) {
            const confirmed = await this.confirmDestructive(
                'Eliminare la riga ' + line.lineId + '?',
                'La quantità verrà azzerata e la riga annullata.',
            );
            if (!confirmed) {
                return;
            }
            await this.postReduce(line.lineId, 0, true);
        },
        async confirmDestructive(title, text) {
            if (typeof window.Swal === 'undefined') {
                return window.confirm(title);
            }
            const result = await window.Swal.fire({
                title,
                text,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Elimina',
                cancelButtonText: 'Annulla',
                confirmButtonColor: '#d33',
            });
            return !!result.isConfirmed;
        },
        async postReduce(codiceRiga, quantity, cancel) {
            this.busy = true;
            this.errorMessage = null;
            this.successMessage = null;
            try {
                await this.postJson(this.order.endpoints.reduceLine, {
                    codice_riga: codiceRiga,
                    quantity,
                    cancel,
                });
                this.successMessage = 'Linea aggiornata.';
                this.editingLineId = null;
                window.setTimeout(() => window.location.reload(), 600);
            } catch (err) {
                this.errorMessage = (err && err.message) || 'Operazione fallita.';
            } finally {
                this.busy = false;
            }
        },
        async postJson(url, body) {
            if (!url) {
                throw new Error('Endpoint non configurato.');
            }
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': this.order.csrfToken || '',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(body),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload.success === false) {
                throw new Error(payload.message || ('HTTP ' + response.status));
            }
            return payload;
        },
    },
};
</script>
